import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { Grid, IconButton, Icons, Stack, Typography } from '@curves/ui-base';
import useProductApi from '@Apis/useProductApi';
import { PageLayout } from '@Local/Components/Layout';
import useHasMenuPermission from '@Util/hook/useHasMenuPermission';
import { FRONT_KEY } from '@Constants/frontKey';
import ProductDetailBasicInfoCard from '@Local/Product/Detail/ProductDetailBasicInfoCard';
import ProductDetailVariantCard from '@Local/Product/Detail/ProductDetailVariantCard';
import { PRODUCT_EDIT_ROUTER_URL, PRODUCT_OVERVIEW_ROUTER } from '@Router/routerPath';

/** 商品明細頁，顯示基本資料與各規格價格 */
const ProductDetailPage = () => {
    const { productID } = useParams();
    const { getProductDetailApi } = useProductApi();
    // 商品明細資料
    const [product, setProduct] = useState(null);
    // 是否載入中
    const [isLoading, setIsLoading] = useState(true);

    // 載入商品明細
    const fetchProductDetail = useCallback(async () => {
        setIsLoading(true);
        const detail = await getProductDetailApi(productID);
        setProduct(detail || null);
        setIsLoading(false);
    }, [getProductDetailApi, productID]);

    useEffect(() => {
        fetchProductDetail();
    }, [fetchProductDetail]);

    // 是否有新增／編輯權限
    const canEdit = useHasMenuPermission(FRONT_KEY.PRODUCT_EDIT);

    const pageActions = [
        {
            label: '返回總覽',
            variant: 'contained',
            as: NavLink,
            to: PRODUCT_OVERVIEW_ROUTER,
        },
    ];

    return (
        <PageLayout>
            <PageLayout.Header title="商品明細" actions={pageActions}>
                {canEdit && (
                    <IconButton component={NavLink} to={PRODUCT_EDIT_ROUTER_URL(productID)} color="primary" aria-label="編輯商品" className="ml-1">
                        <Icons.Edit titleAccess="編輯商品" />
                    </IconButton>
                )}
            </PageLayout.Header>

            {isLoading && <Typography>商品資料載入中...</Typography>}
            {!isLoading && !product && <Typography>查無商品資料。</Typography>}
            {!isLoading && product && (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <ProductDetailBasicInfoCard
                            productName={product.productName}
                            categoryPath={product.saleCategoryName}
                            isEnabled={product.isEnabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h6" className="text-primary font-weight-bold mb-3">
                            商品規格
                        </Typography>
                        <Stack direction="column" spacing={2}>
                            {(product.variants || []).map((variant) => (
                                <ProductDetailVariantCard key={variant.sku} variant={variant} isMain={variant.sku === product.mainSKU} />
                            ))}
                            {(product.variants || []).length === 0 && <Typography>目前沒有商品規格。</Typography>}
                        </Stack>
                    </Grid>
                </Grid>
            )}
        </PageLayout>
    );
};

export default ProductDetailPage;
